/**
 * Confirmación con el estilo de los modales de Atlas (fondo oscurecido, tarjeta, botón
 * primario), en vez del `window.confirm` del navegador. Uso:
 *   const { confirmar, modal } = useConfirmar();
 *   if (await confirmar({ titulo, texto, aceptar: 'Borrar', peligro: true })) ...
 * y `{modal}` en el JSX del componente (se portalea a body, da igual dónde).
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, HelpCircle } from 'lucide-react';

export interface OpcionesConfirmar {
  titulo: string;
  texto?: string;
  aceptar?: string;
  cancelar?: string;
  /** Acción destructiva: botón rojo e ícono de alerta. */
  peligro?: boolean;
}

interface Pendiente extends OpcionesConfirmar {
  resolver: (ok: boolean) => void;
}

export function useConfirmar(): { confirmar: (o: OpcionesConfirmar) => Promise<boolean>; modal: ReactNode } {
  const [pendiente, setPendiente] = useState<Pendiente | null>(null);
  const confirmar = useCallback(
    (o: OpcionesConfirmar) =>
      new Promise<boolean>((resolver) => {
        setPendiente((prev) => {
          prev?.resolver(false);
          return { ...o, resolver };
        });
      }),
    [],
  );
  const cerrar = useCallback((ok: boolean) => {
    setPendiente((p) => {
      p?.resolver(ok);
      return null;
    });
  }, []);
  const modal = pendiente ? <ModalConfirmar {...pendiente} onCerrar={cerrar} /> : null;
  return { confirmar, modal };
}

function ModalConfirmar({ titulo, texto, aceptar = 'Aceptar', cancelar = 'Cancelar', peligro = false, onCerrar }: OpcionesConfirmar & { onCerrar: (ok: boolean) => void }) {
  const primarioRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    primarioRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCerrar(false);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        onCerrar(true);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onCerrar]);
  const Icono = peligro ? AlertTriangle : HelpCircle;
  return createPortal(
    <div className="fixed inset-0 z-[100001] flex items-center justify-center bg-brand-black/60 p-4 backdrop-blur-sm" onMouseDown={() => onCerrar(false)}>
      <div role="dialog" aria-modal="true" aria-labelledby="flujos-confirmar-titulo" className="w-full max-w-md rounded-2xl bg-surface-card p-5 shadow-[0_16px_48px_rgb(var(--brand-black-rgb)_/_0.2)]" onMouseDown={(e) => e.stopPropagation()}>
        <div className="mb-4 flex justify-center">
          <span className={`inline-flex h-12 w-12 items-center justify-center rounded-full ${peligro ? 'bg-[#FEE7E7] text-[#B91C1C]' : 'bg-brand-primary/10 text-brand-primary'}`}>
            <Icono className="h-6 w-6" />
          </span>
        </div>
        <h3 id="flujos-confirmar-titulo" className="mb-2 text-center text-[17px] font-semibold text-brand-black/90">
          {titulo}
        </h3>
        {texto && <p className="mb-6 text-center text-[15px] text-neutral-600">{texto}</p>}
        <div className="flex justify-center gap-2">
          <button
            type="button"
            onClick={() => onCerrar(false)}
            className="inline-flex h-10 items-center justify-center rounded-md border border-neutral-200 bg-white px-5 text-[15px] font-medium text-brand-black/80 transition-colors hover:bg-surface-elevated"
          >
            {cancelar}
          </button>
          <button
            ref={primarioRef}
            type="button"
            onClick={() => onCerrar(true)}
            className={`inline-flex h-10 items-center justify-center rounded-md px-6 text-[15px] font-medium text-white shadow-sm transition-colors ${peligro ? 'bg-[#DC2626] hover:bg-[#B91C1C]' : 'bg-brand-primary hover:bg-brand-primary-hover'}`}
          >
            {aceptar}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
